import React from 'react';
import { Link } from 'react-router-dom';
import { Palmtree, Phone } from 'lucide-react';
import { SITE, waLink } from '@/config';
import { RERA_LIST } from '@/data/siteData';
import { WhatsAppIcon } from '@/components/CTAButtons';
import { trackEvent } from '@/lib/tracking';

const LINKS = [
  { label: 'Overview', to: '/overview' },
  { label: '1 BHK Homes', to: '/1-bhk' },
  { label: '2 BHK Homes', to: '/2-bhk' },
  { label: '2.5 BHK Homes', to: '/2-5-bhk' },
  { label: 'Floor Plans', to: '/provident-palm-vista-floor-plan' },
  { label: 'Amenities', to: '/provident-palm-vista-amenities' },
  { label: 'Location', to: '/provident-palm-vista-location' },
  { label: 'Gallery', to: '/gallery' },
  { label: 'RERA', to: '/provident-palm-vista-rera' },
  { label: 'FAQs', to: '/faqs' },
  { label: 'Contact', to: '/contact' },
];

const SEO_LINKS = [
  { label: 'Price', to: '/provident-palm-vista-price' },
  { label: 'Brochure', to: '/provident-palm-vista-brochure' },
  { label: 'Reviews', to: '/provident-palm-vista-reviews' },
  { label: 'Flats in Shilphata', to: '/flats-in-shilphata' },
  { label: '1 BHK Flats in Shilphata', to: '/1-bhk-flats-in-shilphata' },
  { label: '2 BHK Flats in Shilphata', to: '/2-bhk-flats-in-shilphata' },
  { label: 'Property in Shilphata', to: '/property-in-shilphata' },
  { label: 'Flats on Kalyan–Shilphata Road', to: '/flats-kalyan-shilphata-road' },
];

const Footer = () => (
  <footer className="bg-palm text-ivory" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-full bg-ivory/10 flex items-center justify-center">
              <Palmtree className="h-[18px] w-[18px] text-gold" strokeWidth={1.75} />
            </span>
            <span className="font-display text-xl font-semibold">Provident <span className="text-gold-grad">Palm Vista</span></span>
          </div>
          <p className="text-sm text-ivory/60 mt-4 leading-relaxed">{SITE.address}</p>
          <div className="flex flex-col gap-2 mt-5">
            <a href={SITE.tel} data-testid="footer-call-link" onClick={() => trackEvent('phone_click', { from: 'footer' })}
              className="inline-flex items-center gap-2 text-sm text-ivory/80 hover:text-gold transition-colors">
              <Phone className="h-4 w-4 text-gold" /> Call: {SITE.phoneDisplay}
            </a>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link"
              onClick={() => trackEvent('whatsapp_click', { from: 'footer' })}
              className="inline-flex items-center gap-2 text-sm text-ivory/80 hover:text-gold transition-colors">
              <WhatsAppIcon className="h-4 w-4 text-gold" /> WhatsApp: {SITE.phoneDisplay}
            </a>
          </div>
        </div>
        <div className="md:col-span-3">
          <h3 className="text-xs font-bold tracking-[0.25em] uppercase text-gold">Explore</h3>
          <ul className="mt-4 space-y-2.5">
            {LINKS.map((l) => (
              <li key={l.to}><Link to={l.to} className="text-sm text-ivory/70 hover:text-gold transition-colors">{l.label}</Link></li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <h3 className="text-xs font-bold tracking-[0.25em] uppercase text-gold">Buyer Resources</h3>
          <ul className="mt-4 space-y-2.5">
            {SEO_LINKS.map((l) => (
              <li key={l.to}><Link to={l.to} className="text-sm text-ivory/70 hover:text-gold transition-colors">{l.label}</Link></li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-2">
          <h3 className="text-xs font-bold tracking-[0.25em] uppercase text-gold">RERA</h3>
          <ul className="mt-4 space-y-1.5 text-[11px] text-ivory/50 leading-relaxed">
            {RERA_LIST.map((r) => <li key={r.rera}>{r.wing}: {r.rera}</li>)}
          </ul>
          <p className="text-[11px] text-ivory/50 mt-3">maharera.mahaonline.gov.in</p>
        </div>
      </div>
      <div className="palm-divider my-10 opacity-40" />
      <p className="text-[11px] leading-relaxed text-ivory/45" data-testid="footer-disclaimer">
        Project information, specifications, plans, amenities, images, dimensions, pricing and availability are subject to change and should be independently verified with the authorised sales representative. Images and visual representations may be artistic or illustrative. Please refer to the applicable project documentation and agreement for definitive details. This is an independent information website and not the official developer website.
      </p>
      <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
        <p className="text-xs text-ivory/40">© {new Date().getFullYear()} providentpalmvistashilphata.com</p>
        <div className="flex gap-5">
          <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-xs text-ivory/50 hover:text-gold transition-colors">Privacy Policy</Link>
          <Link to="/terms-conditions" data-testid="footer-terms-link" className="text-xs text-ivory/50 hover:text-gold transition-colors">Terms & Conditions</Link>
          <Link to="/disclaimer" data-testid="footer-disclaimer-link" className="text-xs text-ivory/50 hover:text-gold transition-colors">Disclaimer</Link>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
